import { useState, useEffect } from 'react';
import { Clock, AlertTriangle, Calendar, FileText, Gavel, Euro } from 'lucide-react';
import '../styles/EaaCountdown.scss';

const EaaCountdown = () => {
  return (
    <section id="eaa-countdown" className="eaa-countdown" aria-labelledby="eaa-heading">
      <div className="eaa-countdown__bg-gradient" aria-hidden="true"></div>
      <div className="eaa-countdown__bg-image" aria-hidden="true"></div>
      <div className="eaa-countdown__animated-bg" aria-hidden="true">
        <div className="eaa-countdown__animated-bg-overlay"></div>
        <div className="eaa-countdown__radial-gradient"></div>
        <div className="eaa-countdown__blur-overlay"></div>

        {/* Animated particles */}
        <div className="eaa-countdown__particle-container" aria-hidden="true">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className={`eaa-countdown__particle eaa-countdown__particle-${(i % 5) + 1}`}
              style={{
                width: `${Math.floor(Math.random() * 10) + 5}px`,
                height: `${Math.floor(Math.random() * 10) + 5}px`,
                left: `${Math.floor(Math.random() * 100)}%`,
                top: `${Math.floor(Math.random() * 100)}%`,
                animationDelay: `${Math.floor(Math.random() * 5)}s`,
                opacity: 0.1 + Math.random() * 0.3
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="eaa-countdown__container">
        <header className="eaa-countdown__header">
          <h2 id="eaa-heading" className="eaa-countdown__title">
            The European Accessibility Act (EAA)
          </h2>
          <p className="eaa-countdown__subtitle">
            EU legislation now in effect requiring digital accessibility compliance for websites and applications.
          </p>
        </header>

        {/* EAA Compliance Notice */}
        <div className="eaa-countdown__timer-wrapper" role="alert" aria-label="EAA deadline has passed">
          <div className="eaa-countdown__timer-container">
            <h3 className="eaa-countdown__timer-heading">EAA Compliance Required - Act Now</h3>

            <div
              className="eaa-countdown__deadline-reached"
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                background: 'rgba(31, 74, 175, 0.15)',
                borderRadius: '12px',
                border: '2px solid rgba(96, 101, 209, 0.4)',
                margin: '20px 0'
              }}
            >
              <AlertTriangle
                size={48}
                style={{
                  color: '#8aa1ff',
                  marginBottom: '16px',
                  display: 'block',
                  margin: '0 auto 16px'
                }}
                aria-hidden="true"
              />
              <p
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#FFFFFF',
                  margin: '0 0 12px 0'
                }}
              >
                The European Accessibility Act is now in full effect as of June 28, 2025.
              </p>
              <p
                style={{
                  fontSize: '16px',
                  color: '#FFFFFF',
                  margin: '0 0 20px 0',
                  fontWeight: '500'
                }}
              >
                Don't risk penalties – ensure your website or application meets the legal requirements.
              </p>
              <a
                href="/#contact"
                style={{
                  color: '#8aa1ff',
                  textDecoration: 'underline',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLAnchorElement;
                  target.style.color = '#8aa1ff';
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLAnchorElement;
                  target.style.color = '#8aa1ff';
                }}
                aria-label="Contact us for accessibility compliance assistance"
              >
                Get Compliance Help Now →
              </a>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="eaa-countdown__info-cards-wrapper">
          <div className="eaa-countdown__info-cards-container">
            <div className="eaa-countdown__card-bg-gradient" aria-hidden="true"></div>

            {/* Content Container */}
            <div className="eaa-countdown__card-content">
              <h3 id="eaa-info-heading" className="sr-only">
                EAA Information
              </h3>
              <div className="eaa-countdown__card-grid" role="region" aria-labelledby="eaa-info-heading">
                {/* Card 1 */}
                <article className="eaa-countdown__card">
                  <header className="eaa-countdown__card-header">
                    <div className="eaa-countdown__card-icon-wrapper">
                      <FileText className="eaa-countdown__card-icon" aria-hidden="true" />
                    </div>
                  </header>
                  <h4 className="eaa-countdown__card-title">What is the EAA?</h4>
                  <p className="eaa-countdown__card-text">
                    The European Accessibility Act (EAA), adopted in 2019, is a directive aimed at harmonizing
                    accessibility standards across the EU for products and services.
                  </p>
                </article>

                {/* Card 2 */}
                <article className="eaa-countdown__card">
                  <header className="eaa-countdown__card-header">
                    <div className="eaa-countdown__card-icon-wrapper">
                      <Calendar className="eaa-countdown__card-icon" aria-hidden="true" />
                    </div>
                  </header>
                  <h4 className="eaa-countdown__card-title">Implementation Timeline</h4>
                  <p className="eaa-countdown__card-text">
                    Businesses from EU member states are required to ensure full accessibility compliance by June 2025.
                  </p>
                </article>

                {/* Card 3 */}
                <article className="eaa-countdown__card">
                  <header className="eaa-countdown__card-header">
                    <div className="eaa-countdown__card-icon-wrapper">
                      <Euro className="eaa-countdown__card-icon" aria-hidden="true" />
                    </div>
                  </header>
                  <h4 className="eaa-countdown__card-title">Non-Compliance Penalties</h4>
                  <p className="eaa-countdown__card-text">
                    Organizations failing to meet requirements face significant financial consequences, with fines
                    reaching up to €100,000 for each individual violation.
                  </p>
                </article>

                {/* Card 4 */}
                <article className="eaa-countdown__card">
                  <header className="eaa-countdown__card-header">
                    <div className="eaa-countdown__card-icon-wrapper">
                      <Gavel className="eaa-countdown__card-icon" aria-hidden="true" />
                    </div>
                  </header>
                  <h4 className="eaa-countdown__card-title">Legal Requirements</h4>
                  <p className="eaa-countdown__card-text">
                    Compliance extends to websites, mobile apps, and digital interfaces, requiring organizations to
                    implement accessibility features for users with diverse disabilities.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EaaCountdown;
